import { TravelInfo } from "../../../Repositories/TravelRepository";

/**
 * Etat en mémoire du scénario déplacement pour la déclaration en cours.
 *
 * Volontairement hors Redux : la preuve de déplacement est un objet File
 * (non sérialisable pour redux-persist). En cas de rafraîchissement de page,
 * le scénario est re-vérifié au moment de la déclaration (LastStep) et la
 * preuve est redemandée si nécessaire.
 */
type TravelState = {
    clientId: number | null;
    info: TravelInfo | null;
    proofFile: File | null;
    sameAddressAcknowledged: boolean;
    /** P6 : l'abonnement de transports en commun valide sert de preuve. */
    useSubscription: boolean;
};

const state: TravelState = {
    clientId: null,
    info: null,
    proofFile: null,
    sameAddressAcknowledged: false,
    useSubscription: false,
};

const modeQuestionKey = (teachrId: number, clientId: number) => `travelModeQuestionSkipped_${teachrId}_${clientId}`;

const TravelStore = {
    setInfo(clientId: number, info: TravelInfo) {
        if (state.clientId !== clientId) {
            state.proofFile = null;
            state.sameAddressAcknowledged = false;
            state.useSubscription = false;
        }
        state.clientId = clientId;
        state.info = info;
    },

    // Popup « adresse identique » : affichée une seule fois par client
    // (le scénario est re-vérifié au récapitulatif, sans re-popup).
    acknowledgeSameAddress(clientId: number | null | undefined) {
        if (clientId != null && state.clientId === clientId) {
            state.sameAddressAcknowledged = true;
        }
    },

    wasSameAddressAcknowledged(clientId: number | null | undefined): boolean {
        return clientId != null && state.clientId === clientId && state.sameAddressAcknowledged;
    },

    getInfo(clientId: number | null | undefined): TravelInfo | null {
        return clientId != null && state.clientId === clientId ? state.info : null;
    },

    /** P7 : met à jour une partie des infos (mode choisi, anomalie) sans réinterroger le serveur. */
    patchInfo(clientId: number | null | undefined, patch: Partial<TravelInfo>) {
        if (clientId != null && state.clientId === clientId && state.info) {
            state.info = { ...state.info, ...patch };
        }
    },

    // P7 : la question légère « Comment vous rendez-vous chez cet élève ? » est
    // posée une seule fois par couple prof/client. Le choix est enregistré côté
    // serveur ; un « Passer » est mémorisé dans ce navigateur pour ne pas
    // redemander à chaque déclaration.
    wasModeQuestionSkipped(teachrId: number | null | undefined, clientId: number | null | undefined): boolean {
        if (teachrId == null || clientId == null) return false;
        try {
            return window.localStorage.getItem(modeQuestionKey(teachrId, clientId)) === "1";
        } catch {
            return false;
        }
    },

    markModeQuestionSkipped(teachrId: number | null | undefined, clientId: number | null | undefined) {
        if (teachrId == null || clientId == null) return;
        try {
            window.localStorage.setItem(modeQuestionKey(teachrId, clientId), "1");
        } catch {
            // stockage indisponible : la question sera simplement reposée
        }
    },

    setProofFile(file: File | null) {
        state.proofFile = file;
    },

    // P6 : la preuve du cours sera l'abonnement de transports en commun du
    // prof, rattaché côté serveur après la création du cours.
    setUseSubscription(clientId: number | null | undefined, value: boolean) {
        if (clientId != null && state.clientId === clientId) {
            state.useSubscription = value;
        }
    },

    getUseSubscription(clientId: number | null | undefined): boolean {
        return clientId != null && state.clientId === clientId && state.useSubscription;
    },

    getProofFile(clientId: number | null | undefined): File | null {
        return clientId != null && state.clientId === clientId ? state.proofFile : null;
    },

    reset() {
        state.clientId = null;
        state.info = null;
        state.proofFile = null;
        state.sameAddressAcknowledged = false;
        state.useSubscription = false;
    },
};

export default TravelStore;
